// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { integrationApi } from "api/TenantSlug/Integration/api";
import { composeGetIntegrationQueryOptions } from "api/TenantSlug/Integration/queryOptions.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import StoreIntegrationRequest = App.Http.Requests.Integration.StoreIntegrationRequest;
import PatchIntegrationRequest = App.Http.Requests.Integration.PatchIntegrationRequest;
import GetIntegrationResponse = App.Http.Responses.Integration.GetIntegrationResponse;
import GetConnectionSequenceStepsExportResponse = App.Http.Responses.ConnectionCreator.SequenceStep.GetConnectionSequenceStepsExportResponse;
import GetIntegrationsResponse = App.Http.Responses.Integration.GetIntegrationsResponse;
import GetIntegrationsRequest = App.Http.Requests.Integration.GetIntegrationsRequest;
import StoreIntegrationConnectionSequenceStepsRequest = App.Http.Requests.Integration.StoreIntegrationConnectionSequenceStepsRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import TestConnectionSequenceStepResponse = App.Http.Responses.ConnectionSequenceStep.TestConnectionSequenceStepResponse;
import TestIntegrationRequest = App.Http.Requests.Integration.TestIntegrationRequest;

// Query to get a list of integrations
export const useGetIntegrationsQuery = (request: GetIntegrationsRequest, options = {}) => {
    return useQueryApi<GetIntegrationsResponse>({
        queryKey: ["getIntegrations"],
        queryFn: () => integrationApi.index(request),
        ...options,
    });
};

// Query to get a specific integration
export const useGetIntegrationQuery = (integrationId: string, request?: GetIntegrationsRequest, options = {}) => {
    return useQueryApi<GetIntegrationResponse>({
        ...composeGetIntegrationQueryOptions(integrationId, request),
        ...options,
    });
};

// Query to get a specific integration in export format
export const useGetIntegrationExportQuery = (integrationId: string, request?: GetIntegrationsRequest, options = {}) => {
    return useQueryApi<GetIntegrationResponse>({
        queryKey: ["getIntegrationExport", integrationId],
        queryFn: () => integrationApi.showExport(integrationId, request),
        ...options,
    });
};

// Query to get a specific integration connection sequence steps in export format
export const useGetIntegrationMethodsExportQuery = (
    integrationId: string,
    request?: GetIntegrationsRequest,
    options = {}
) => {
    return useQueryApi<GetConnectionSequenceStepsExportResponse>({
        queryKey: ["getIntegrationMethodsExport", integrationId],
        queryFn: () => integrationApi.showMethodsExport(integrationId, request),
        ...options,
    });
};

// Mutation to store a new integration
export const useStoreIntegrationMutation = (
    options: UseMutationOptions<GetIntegrationResponse, AxiosError<ApiErrorResponse>, StoreIntegrationRequest> = {}
) => {
    return useMutationApi<GetIntegrationResponse, StoreIntegrationRequest>({
        mutationKey: ["storeIntegration"],
        mutationFn: (request: StoreIntegrationRequest) => integrationApi.store(request),
        ...options,
    });
};

// Mutation to update an existing integration using PUT
export const useUpdateIntegrationMutation = (integrationId: string, options = {}) => {
    return useMutationApi<GetIntegrationResponse, StoreIntegrationRequest>({
        mutationKey: ["updateIntegration", integrationId],
        mutationFn: (request: StoreIntegrationRequest) => integrationApi.update(request, integrationId),
        ...options,
    });
};

// Mutation to update an existing integration methods using PUT
export const useUpdateIntegrationMethodsMutation = (integrationId: string, options = {}) => {
    return useMutationApi<GetConnectionSequenceStepsExportResponse, StoreIntegrationConnectionSequenceStepsRequest>({
        mutationKey: ["updateIntegrationMethods", integrationId],
        mutationFn: (request: StoreIntegrationConnectionSequenceStepsRequest) =>
            integrationApi.updateMethods(request, integrationId),
        ...options,
    });
};

// Mutation to partially update an existing integration using PATCH
export const usePatchIntegrationMutation = (integrationId: string, options = {}) => {
    return useMutationApi<GetIntegrationResponse, PatchIntegrationRequest>({
        mutationKey: ["patchIntegration", integrationId],
        mutationFn: (request: PatchIntegrationRequest) => integrationApi.patchUpdate(request, integrationId),
        ...options,
    });
};

// Mutation to destroy an integration
export const useDestroyIntegrationMutation = (integrationId: string, options = {}) => {
    return useMutationApi<void, string>({
        mutationKey: ["destroyIntegration", integrationId],
        mutationFn: () => integrationApi.destroy(integrationId),
        ...options,
    });
};

export const useExportIntegrationsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportIntegrations"],
        mutationFn: (request) => integrationApi.export(request),
        ...options,
    });
};

// Mutation to test integration
export const useTestIntegrationMutation = (
    options: UseMutationOptions<
        TestConnectionSequenceStepResponse,
        AxiosError<ApiErrorResponse>,
        { integrationId: string; request: TestIntegrationRequest }
    > = {}
) => {
    return useMutationApi<TestConnectionSequenceStepResponse, { integrationId: string; request: TestIntegrationRequest }>({
        mutationKey: ["testIntegration"],
        mutationFn: ({ integrationId, request }) => integrationApi.test(integrationId, request),
        ...options,
    });
};
